<template>
  <div
    v-if="isOpen && criterion"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
      <!-- Header: user/team name + criterion -->
      <div class="p-6 border-b">
        <h2 class="text-xl font-bold text-gray-800">
          <span v-if="!anonymousMode && subjectName">{{ subjectName }} – </span>
          {{ criterion.criterion.name }}
        </h2>
      </div>

      <!-- Body: assessment inputs -->
      <div class="p-6 flex flex-col space-y-4">
        <!-- Countable -->
        <div v-if="criterion.criterion.type === 'countable'" class="flex items-center space-x-2">
          <button
            class="px-3 py-1 bg-red-500 text-white rounded"
            @click="decrement"
          >-</button>
          <input
            ref="countInput"
            type="number"
            v-model.number="countDraft"
            class="border rounded p-1 w-16 text-center"
          />
          <button
            class="px-3 py-1 bg-green-500 text-white rounded"
            @click="increment"
          >+</button>
          <!-- Active change indicator -->
          <div v-if="activeChangeValue !== null" class="text-sm font-semibold">
            <span v-if="activeChangeValue > 0" class="text-emerald-600">▲ +{{ activeChangeValue }}</span>
            <span v-else class="text-red-600"> ▼ {{ activeChangeValue }}</span>
          </div>
        </div>

        <!-- Boolean -->
        <div v-else-if="criterion.criterion.type === 'boolean'" class="flex items-center space-x-2">
          <input type="checkbox" v-model="isFulfilledDraft" @change="toggleBoolean" class="h-5 w-5" />
          <span class="text-lg font-semibold">Fulfilled</span>
        </div>

        <!-- Text -->
        <div class="relative w-full">
          <textarea
            ref="textarea"
            v-model="textDraft"
            rows="4"
            placeholder="Optional text..."
            class="border rounded p-2 w-full"
            @focus="showSuggestions = true"
            @blur="hideSuggestions"
          ></textarea>

          <!-- Suggestions Dropdown (mouse selection only) -->
          <ul
            v-show="showSuggestions && filteredSuggestions.length"
            class="absolute z-10 bg-white border rounded shadow mt-1 w-full max-h-40 overflow-auto"
          >
            <li class="px-2 py-1 font-semibold bg-indigo-100 sticky top-0">
              Suggestions
            </li>
            <li
              v-for="(text, index) in filteredSuggestions"
              :key="index"
              class="px-2 py-1 cursor-pointer hover:bg-indigo-100"
              @mousedown.prevent="selectSuggestion(text)"
            >
              {{ text || "(empty)" }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Footer: shortcuts hint + save actions -->
      <div class="flex items-center justify-between gap-4 p-6 border-t bg-gray-50">
        <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span class="flex items-center gap-1">
            <kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">Esc</kbd>
            <span>unfocus input / close modal</span>
          </span>
          <span v-if="criterion.criterion.type  === 'countable'" class="flex items-center gap-1">
            <kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">↑ ↓</kbd>
            <span>increment / decrement</span>
          </span>
          <span class="flex items-center gap-1">
            <kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">A-Z</kbd>
            <span>focus text field</span>
          </span>
          <span class="flex items-center gap-1">
            <kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">Ctrl + Enter</kbd>
            <span>save</span>
          </span>
          <span class="flex items-center gap-1">
            <kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">Ctrl + P</kbd>
            <span>save &amp; publish</span>
          </span>
        </div>
        <div class="flex gap-2 shrink-0">
          <button
            class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >Save</button>
          <button
            class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
            :disabled="saving"
            @click="saveAndPublish"
          >Save &amp; Publish</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { setBooleanValue, setTextAndCountValue, publishUserCriterion } from "@/api/criterias";
import { setTeamBooleanValue, setTeamTextAndCountValue, publishTeamCriterion } from "@/api/teamCriteria";

export default {
  props: {
    isOpen: { type: Boolean, default: false },
    criterion: { type: Object, default: null },
    criteria: { type: Array, default: () => [] },
    subjectName: { type: String, default: "" },
    anonymousMode: { type: Boolean, default: false },
    teamMode: { type: Boolean, default: false },
    userId: { type: Number, required: true },
    teamId: { type: Number, default: null },
    sessionId: { type: Number, required: true },
  },
  emits: ["close", "updated"],
  data() {
    return {
      textDraft: "",
      countDraft: null,
      isFulfilledDraft: false,
      showSuggestions: false,
      saving: false,
    };
  },
  computed: {
    activeChangeValue() {
      if (!this.criterion) return null;
      if (this.criterion.criterion.type !== "countable") return null;

      const current = this.criterion.count_value ?? 0;
      const draft = this.countDraft ?? 0;

      const diff = draft - current;
      return diff !== 0 ? diff : null;
    },
    filteredSuggestions() {
      const allTexts = this.criteria.flatMap(c => c.last_texts || []);
      const uniqueTexts = [...new Set(allTexts)];
      const input = this.textDraft.toLowerCase();
      return uniqueTexts.filter(t => (t || "").toLowerCase().includes(input));
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.initDrafts();
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", this.handleKeydown);
      } else {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", this.handleKeydown);
      }
    },
    criterion: {
      deep: true,
      handler(c) {
        // Note: the parent briefly sets the criterion to null when refreshing
        // after a save — drafts must only sync for a real criterion object.
        if (!c || c.all) return;
        this.countDraft = c.count_value ?? 0;
        this.isFulfilledDraft = !!c.is_fulfilled;
      },
    },
  },
  methods: {
    initDrafts() {
      this.textDraft = "";
      this.countDraft = this.criterion?.count_value ?? 0;
      this.isFulfilledDraft = !!this.criterion?.is_fulfilled;
      this.showSuggestions = false;
    },
    handleKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        const focused = document.activeElement;
        const isInput =
          focused?.tagName === "TEXTAREA" || focused?.tagName === "INPUT";
        if (isInput) {
          focused.blur();
          return;
        }
        this.$emit("close");
        return;
      }

      const focused = document.activeElement;
      const isTextArea = focused?.tagName === "TEXTAREA";
      const isNumberInput = focused?.tagName === "INPUT" && focused?.type === "number";
      const isCountable = this.criterion?.criterion?.type === "countable";

      // --- Ctrl + Enter: Save ---
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault();
        this.save();
        return;
      }

      // --- Ctrl + P: Save & Publish ---
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        this.saveAndPublish();
        return;
      }

      // --- Arrow Up/Down: increment/decrement countable value ---
      if (isCountable && !isTextArea && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
        if (e.key === "ArrowUp") this.increment();
        else this.decrement();
        return;
      }

      // --- Typing any character (incl. digits) focuses the textarea and starts writing ---
      // Exception: when the count input is already focused, digits apply natively there.
      if (!isTextArea && !isNumberInput && !e.ctrlKey && !e.altKey && !e.metaKey && e.key.length === 1) {
        e.preventDefault();
        const textarea = this.$refs.textarea;
        if (textarea) {
          textarea.focus();
          this.textDraft += e.key;
        }
      }
    },
    increment() {
      if (!this.criterion || this.criterion.criterion.type === "boolean") return;
      this.countDraft = (this.countDraft ?? 0) + 1;
    },
    decrement() {
      if (!this.criterion || this.criterion.criterion.type === "boolean") return;
      this.countDraft = (this.countDraft ?? 0) - 1;
    },
    selectSuggestion(text) {
      this.textDraft = text;
      this.showSuggestions = false;
    },
    hideSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 100);
    },
    async toggleBoolean() {
      if (!this.criterion) return;
      try {
        if (this.teamMode && this.teamId) {
          await setTeamBooleanValue(this.criterion.criterion_id, this.teamId, this.sessionId, this.isFulfilledDraft);
        } else {
          await setBooleanValue(this.criterion.criterion_id, this.userId, this.sessionId, this.isFulfilledDraft);
        }
        this.$emit("updated");
      } catch (err) {
        console.error("Failed to toggle boolean:", err);
        this.isFulfilledDraft = !this.isFulfilledDraft;
      }
    },
    async persistTextAndCount() {
      const textToSend = this.textDraft?.trim() || undefined;
      const isCountable = this.criterion.criterion.type === "countable";
      // For countable criteria always send the count (even 0) so the history
      // entry records an int_value instead of falling back to the boolean state.
      const countToSend = isCountable ? (this.countDraft ?? 0) : undefined;

      if (textToSend === undefined && countToSend === undefined) return false;

      if (this.teamMode && this.teamId) {
        await setTeamTextAndCountValue(
          this.criterion.criterion_id,
          this.teamId,
          this.sessionId,
          textToSend,
          countToSend
        );
      } else {
        await setTextAndCountValue(
          this.criterion.criterion_id,
          this.userId,
          this.sessionId,
          textToSend,
          countToSend
        );
      }

      this.textDraft = "";
      return true;
    },
    async save() {
      if (!this.criterion || this.saving) return;
      this.saving = true;
      try {
        if (await this.persistTextAndCount()) {
          this.$emit("updated");
        }
        this.$emit("close");
      } catch (err) {
        console.error("Failed to save:", err);
      } finally {
        this.saving = false;
      }
    },
    async saveAndPublish() {
      if (!this.criterion || this.saving) return;
      this.saving = true;
      try {
        await this.persistTextAndCount();
        if (this.teamMode && this.teamId) {
          await publishTeamCriterion(this.criterion.criterion_id, this.teamId, this.sessionId);
        } else {
          await publishUserCriterion(this.criterion.criterion_id, this.userId, this.sessionId);
        }
        this.$emit("updated");
        this.$emit("close");
      } catch (err) {
        console.error("Failed to save and publish:", err);
      } finally {
        this.saving = false;
      }
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    window.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped>
kbd {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
</style>
